import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom';
import AppBase from '../app/AppBase';
import ThankYouPage from '../../views/contact/ThankYouPage';
import AuthenticatedRoute from './AuthenticatedRoute';
import ReadinessGaugeDashboard from '../../views/readiness_gauge/ReadinessGaugeDashboard';
import MetricCollectionDataManagement from '../../views/metric_collection/MetricCollectionDataManagement';
import MetricCollectionAuditLogs from '../../views/metric_collection/MetricCollectionAuditLogs';
import ReadinessGaugeQuestionnaire from '../../views/readiness_gauge/ReadinessGaugeQuestionnaire';
import ReadinessGaugeResults from '../../views/readiness_gauge/ReadinessGaugeResults';
import AssessmentDashboard from '../../views/assessment/AssessmentDashboard';
import ResourceCentreHome from '../../views/resource_centre/ResourceCentreHome';
import ConfigurationDashboard from '../../views/configuration/ConfigurationDashboard';
import ConfigurationPersonalisation from '../../views/configuration/ConfigurationPersonalisation';
import ConfigurationGeographical from '../../views/configuration/ConfigurationGeographical';
import ConfigurationOrganisation from '../../views/configuration/ConfigurationOrganisation';
import ConfigurationExternalCompanies from '../../views/configuration/ConfigurationExternalCompanies';
import ConfigurationReportingPeriods from '../../views/configuration/ConfigurationReportingPeriods';
import ConfigurationReportingPeriodGroups from '../../views/configuration/ConfigurationReportingPeriodGroups';
import ConfigurationStandards from '../../views/configuration/ConfigurationStandards';
import ConfigurationMetrics from '../../views/configuration/ConfigurationMetrics';
import ConfigurationOtherConfiguration from '../../views/configuration/ConfigurationOtherConfiguration';
import ConfigurationEmissionFactors from '../../views/configuration/ConfigurationEmissionFactors';
import GroupManagementView from '../../views/group_management/GroupManagement';
import GroupView from '../../views/group_management/GroupView';
import UserManagementView from '../../views/user_management/UserManagementView';
import InvalidURL from '../../views/shared/InvalidURL';
import MasterViewDashboard from '../../views/master_view/MasterViewDashboard';
import ContactWrapper from '../contact/ContactWrapper';
import IndexView from '../../views/IndexView';
import { DrawerLeftProvider } from '../../context/DrawerLeftContext';
import FAQPage from '../../views/faq/FAQPage';
import ResourceCentreDetails from '../../views/resource_centre/ResourceCentreDetails';
import { AuthActionRedirector } from '../app/authentication/AuthActionRedirector';
import ConfigurationCarbonTargets from '../../views/configuration/ConfigurationCarbonTargets';
import DataImport from '../../views/data_import/DataImport';
import MetricCollectionDataCompleteness from '../../views/metric_collection/MetricCollectionDataCompleteness';
import DefaultErrorBoundary from '../../views/shared/DefaultErrorBoundary';
import DataExport from '../../views/data_export/DataExport';
import ExpiredLink from '../../views/password-management/ExpiredLink';
import DataIngestion from '../../views/syntiro_honey/DataIngestion';
import QuantifiedRiskView from '../../views/forecasting_risk/QuantifiedRiskView';
import ForecastingView from '../../views/forecasting_risk/ForecastingView';
import { ENABLE_SYNTIRO_HONEY_MODULE } from '../../env';
import PasswordResetPage from '../../views/password-management/PasswordResetPage';
import PasswordSetUpPage from '../../views/password-management/PasswordSetUpPage';

export const default_authenticated_home_url = '/';

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/"
      element={
        <DrawerLeftProvider>
          <AppBase />
        </DrawerLeftProvider>
      }
      errorElement={<DefaultErrorBoundary />}
    >
      <Route path="/contact" element={<ContactWrapper />} />
      <Route index element={<IndexView />} />
      <Route path="/thank-you" element={<ThankYouPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="web/reporting" element={<p>Report</p>} />
      <Route path="web/readiness" element={<p>Readiness</p>} />
      <Route path="web/resources" element={<p>Resources</p>} />
      <Route path="web/assessment" element={<p>Assessment</p>} />
      <Route path="web/data" element={<p>Data</p>} />
      <Route path="/auth_action" element={<AuthActionRedirector />} />
      <Route path="/link-expired" element={<ExpiredLink />} />
      <Route path="/reset-password" element={<PasswordResetPage />} />
      <Route path="/set-up-password" element={<PasswordSetUpPage />} />
      <Route path="/recover-password" element={<IndexView />} />
      <Route path="/*" element={<InvalidURL />} />
      <Route element={<AuthenticatedRoute />}>
        <Route path="/trading/customer_dashboard" element={null} />
        <Route path="/trading/generator_dashboard" element={null} />
        {/* Generator allocation files points to default error boundary as a placeholder,
        nobody should have access to this yet. */}
        <Route path="/trading/generator_allocation_files" element={<InvalidURL />} />
        <Route path="/sustainability/readiness/" element={<ReadinessGaugeDashboard />} />
        <Route path="/sustainability/carbon_dashboard" element={null} />
        <Route path="/sustainability/esg_dashboard" element={null} />
        <Route path="/sustainability/metrics" element={<MetricCollectionDataManagement />} />
        <Route
          path="/sustainability/metrics/data_completeness"
          element={<MetricCollectionDataCompleteness />}
        />
        <Route path="/metrics/audit" element={<MetricCollectionAuditLogs />} />
        <Route path="/sustainability/readiness/" element={<ReadinessGaugeDashboard />} />
        <Route
          path="/sustainability/readiness/questionnaire"
          element={<ReadinessGaugeQuestionnaire />}
        />
        {/*
          Dashboard routes intentionally render `null`. The actual dashboard content
          is rendered by <DashboardRenderLayer /> (mounted in AppBase, outside the
          Outlet) which keys off `pathname.split('/').pop()`. These route entries
          exist so React Router treats the URLs as valid and doesn't fall through
          to the catch-all <InvalidURL />.
        */}
        <Route path="/sustainability/readiness/results" element={<ReadinessGaugeResults />} />
        <Route path="/sustainability/assessment" element={<AssessmentDashboard />} />
        <Route path="/resources/explore/" element={<ResourceCentreHome />} />
        <Route path="/resources/details" element={<ResourceCentreDetails />} />
        <Route path="/sustainability/import" element={<DataImport />} />
        {ENABLE_SYNTIRO_HONEY_MODULE && (
          <Route path="/syntiro-honey/data-ingestion" element={<DataIngestion />} />
        )}
        <Route path="/utility_management/energy_dashboard" element={null} />
        <Route path="/utility_management/water_dashboard" element={null} />
        <Route path="/utility_management/gas_dashboard" element={null} />
        <Route path="/utility_management/strategic_dashboard" element={null} />
        <Route path="/utility_management/tenant_dashboard" element={null} />
        <Route path="/utility_management/operational_dashboard" element={null} />
        <Route path="/utility_management/custom_dashboard" element={null} />
        <Route path="/asset_management/data_quality" element={null} />
        <Route path="/asset_management/management_overview" element={null} />
        <Route path="/exports/manage" element={<DataExport />} />
        <Route path="/configuration" element={<ConfigurationDashboard />} />
        <Route path="/configuration/personalisation" element={<ConfigurationPersonalisation />} />
        <Route path="/configuration/geographical" element={<ConfigurationGeographical />} />
        <Route path="/configuration/organisation" element={<ConfigurationOrganisation />} />
        <Route
          path="/configuration/external-companies"
          element={<ConfigurationExternalCompanies />}
        />
        <Route
          path="/configuration/reporting-periods"
          element={<ConfigurationReportingPeriods />}
        />
        <Route
          path="/configuration/reporting-period-groups"
          element={<ConfigurationReportingPeriodGroups />}
        />
        <Route path="/configuration/standards" element={<ConfigurationStandards />} />
        <Route path="/configuration/metrics" element={<ConfigurationMetrics />} />
        <Route
          path="/configuration/other-configuration"
          element={<ConfigurationOtherConfiguration />}
        />
        <Route path="/configuration/emission-factors" element={<ConfigurationEmissionFactors />} />
        <Route path="/configuration/carbon-targets" element={<ConfigurationCarbonTargets />} />
        <Route path="/settings/group-management" element={<GroupManagementView />} />
        <Route path="/settings/group-management/view" element={<GroupView />} />
        <Route path="/settings/user-management" element={<UserManagementView />} />
        <Route path="/settings/master/dashboard" element={<MasterViewDashboard />} />
        <Route path="/asset_management/bulk_water_tanks" element={null} />
        <Route path="/asset_management/oxygen" element={null} />
        <Route path="/asset_management/autoclaves" element={null} />
        <Route path="/asset_management/vacuum_pumps" element={null} />
        <Route path="/asset_management/solar_pv" element={null} />
        <Route path="/asset_management/air_handling_units" element={null} />
        <Route path="/asset_management/general_facility" element={null} />
        <Route path="/asset_management/bess" element={null} />
        <Route path="/asset_management/borehole_and_ro_plants" element={null} />
        <Route path="/asset_management/diesel_generators" element={null} />
        <Route path="/asset_management/chilled_water_plants" element={null} />
        <Route path="/asset_management/lighting_systems" element={null} />
        <Route path="/asset_management/refrigeration_plants" element={null} />
        <Route path="/asset_management/refrigeration_plants_caas" element={null} />
        <Route path="/asset_management/hot_water_plants" element={null} />
        <Route path="/asset_management/gas_storage_systems" element={null} />
        <Route path="/asset_management/steam_generation_plants" element={null} />
        <Route path="/asset_management/compressed_air_systems" element={null} />
        <Route path="/asset_management/fresh_air_pretreatment" element={null} />
        <Route path="forecasting_risk">
          <Route path="quantified_risk" element={<QuantifiedRiskView />} />
          <Route path="forecasting" element={<ForecastingView />} />
        </Route>
      </Route>
    </Route>
  ),
  {
    future: {
      v7_relativeSplatPath: true
    }
  }
);
